/* eslint-disable max-len */
/**
@format
@flow
*/
import React, { useState } from 'react';
import Colors from '@ziynx/theme/colors';
import { Constants } from '@ziynx/components/layout';
import styled from 'styled-components';
import { ButtonIcon as KrispyButtonIcon } from '@krispy/buttons/web';
import Icon from '@ziynx/components/icons';
import { getDimensions, LoadingSpinner } from '@ziynx/components/utilities';

export type Props = $ReadOnly<{|
  /**
   * Disabled treatement (both visually and functionally) for our Primary button.
   */
  disabled?: boolean,

  /**
   * If button is inverted. Background is white
   */
  inverted?: boolean,

  /**
   * Depicts when button is in loading state
   */
  isLoading?: boolean,

  /**
   * Depicts if button is last child in ButtonGroup
   */
  lastChild?: boolean,

  /**
   * Depicts if button should be rendered as an anchor
   */
  isLink?: boolean,

  /**
   * Href for button as a link
   */
  href?: string,

  /**
   * Target for href
   */
  target?: '_blank' | '_self',

  /**
  * Icon for the button
  */
  icon?: string,

  /**
  * Set a custom width for button.
  */
  width?: string | number,

  /**
  * Set a custom width for button.
  */
  height?: string | number,

  /**
  * Set a custom width for anchor.
  */
  anchorWidth?: string,

  /**
   * BackgroundColor for button, if any
   * Defaults to blue if active and GrayLight if disabled
   */
  backgroundColor?: string,

  /**
  * Color for icon
  * Defaults to White
  */
  iconColor?: string,

  /**
   * Size of icon
   * 'veryMini' | 'mini' | 'veryTiny' | 'tiny' | 'extraSmall' | 'small' | 'medium' | 'large'
   * Defaults to tiny
   */
  iconSize?: string,

  /**
   * Border color if hasBorder is true
   * Defaults to GrayDark
   */
  borderColor?: string,

  /**
  * Set a function to fire when the button is clicked.
  */
  onClick?: (event: SyntheticEvent<HTMLAnchorElement>) => any | Promise<any>;
  /**
   * Controls whether button uses width or min-width flex responsiveness
  */
  minWidth?: string | number,
|}>;

// eslint-disable-next-line max-len
const findBackgroundColor = (inverted, disabled, isLoading, isHovering, backgroundColor): string => {
  if (inverted || (inverted && disabled)) {
    return Colors.White;
  } if (disabled) {
    return Colors.GrayLight;
  } if (isLoading) {
    return Colors.BrandBlueFaded;
  } if (isHovering) {
    return Colors.BrandBlueDark;
  }
  return backgroundColor || Colors.PrimaryBlue;
};
const findBorderColor = (inverted, disabled, isLoading, borderColor): string => {
  if (inverted && !disabled && !isLoading) {
    return Colors.GrayDark;
  } if ((inverted && disabled) || (inverted || isLoading)) {
    return Colors.GrayLight;
  }
  return borderColor || '';
};

const findIconColor = (iconColor, inverted, disabled, isLoading, backgroundColor): string => {
  if (inverted && !disabled && !isLoading) {
    return backgroundColor || Colors.GrayDark;
  } if ((inverted && disabled) || (inverted && isLoading)) {
    return Colors.GrayLight;
  }
  return iconColor || Colors.White;
};

const StyledKrispyButton = styled(KrispyButtonIcon)`
  ${({ lastChild }) => !lastChild && 'margin-right: 12px'};
  min-width: 78px;
  max-width: 312px;
  box-sizing: border-box;
`;

const IconAnchor = styled.a`
  display: inline-block;
  height: ${({ height }) => height};
  cursor: ${({ disabled, isLoading }) => ((disabled || isLoading) ? 'auto' : 'pointer')}};
  pointer-events:${({ disabled, isLoading }) => (disabled || isLoading ? 'none' : null)}};
  border-radius: 16px;
  background-color: ${({
    inverted, disabled, isLoading, backgroundColor, isHovering,
  }) => findBackgroundColor(inverted, disabled, isLoading, isHovering, backgroundColor)}};
  padding: ${({ inverted }) => (inverted ? 10 : 12)}px;
  border: ${({ inverted, disabled, isLoading }) => (inverted ? `2px solid ${findBorderColor(inverted, disabled, isLoading)}` : null)}};
  ${({
    borderColor, inverted,
  }) => borderColor && inverted && `
    -webkit-box-sizing: border-box;
    -moz-box-sizing: border-box;
    box-sizing: border-box;
  `}
  box-shadow: ${({ inverted }) => !inverted && Constants.boxShadow};
  transition: ease-in-out 200ms;
  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  box-sizing: border-box;
  `;

const IconWrapper = styled.span`
  display: flex;
  justify-content: center;
  width: ${({ width }) => width};
  align-items: center;
`;

const ButtonIcon = (props: Props): React$Node => {
  const {
    icon,
    disabled,
    inverted,
    isLoading,
    lastChild,
    isLink,
    href,
    anchorWidth,
    borderColor,
    backgroundColor,
    iconColor,
    iconSize = 'tiny',
    onClick,
    height,
    width,
    minWidth,
    target,
    ...rest
  } = props;

  const [isHovering, setHovering] = useState(false);

  const handleOnClick = (event): void => {
    if (isHovering) setHovering(false);
    if (onClick) onClick(event);
  };

  const handleOnMouseEnter = (): void => {
    setHovering(true);
  };

  const handleOnMouseLeave = (): void => {
    setHovering(false);
  };

  const ButtonWidth = getDimensions(width);
  const ButtonHeight = getDimensions(height);

  return (
    !href && !isLink
      ? (
        <StyledKrispyButton
          width={ButtonWidth}
          minWidth={ButtonWidth}
          height={ButtonHeight}
          disabled={disabled}
          inverted={inverted}
          isLoading={isLoading}
          isHovering={isHovering}
          lastChild={lastChild}
          iconSize={iconSize}
          icon={icon}
          backgroundColor={findBackgroundColor(inverted, disabled, isLoading, isHovering, backgroundColor)}
          borderColor={findBorderColor(inverted, disabled, isLoading, borderColor)}
          iconColor={findIconColor(iconColor, inverted, disabled, isLoading, backgroundColor)}
          onClick={handleOnClick}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
          {...rest}
        >
          <LoadingSpinner color={inverted ? Colors.GrayLight : Colors.White} />
        </StyledKrispyButton>
      )
      : (
        <IconAnchor
          href={href}
          height={ButtonHeight}
          disabled={disabled}
          inverted={inverted}
          isLoading={isLoading}
          isHovering={isHovering}
          backgroundColor={findBackgroundColor(inverted, disabled, isLoading, isHovering, backgroundColor)}
          borderColor={findBorderColor(inverted, disabled, isLoading, borderColor)}
          onClick={handleOnClick}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
          applyTransition={inverted && !disabled && !isLoading && isHovering}
          target={target}
          {...rest}
        >
          <IconWrapper width={anchorWidth}>
            {!isLoading ? (
              <Icon
                name={icon}
                color={findIconColor(iconColor, inverted, disabled, isLoading, backgroundColor)}
                {...{ [iconSize]: true }}
              />
            ) : (
              <LoadingSpinner color={inverted ? Colors.GrayLight : Colors.White} />
            )}
          </IconWrapper>
        </IconAnchor>
      )
  );
};

ButtonIcon.defaultProps = {
  onClick: (): void => {},
  anchorWidth: '54px',
  width: '78px',
  height: '46px',
  minWidth: undefined,
  disabled: false,
  isLoading: false,
  lastChild: false,
  inverted: false,
  icon: 'email',
  iconColor: Colors.White,
  iconSize: 'tiny',
  target: '_blank',
  href: undefined,
};

export default ButtonIcon;
